import { channelService } from "@/services/channel.service/channel.api";
import type { Channel } from "@/types/channel";
import { useEffect, useState } from "react";
import Select from "react-select";

interface ChannelsSelectProps {
  selectedChannels: string[];
  onChange: (channels: string[]) => void;
}

export default function ChannelsSelect({
  selectedChannels,
  onChange,
}: ChannelsSelectProps) {
  const [channels, setChannels] = useState<Channel[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchChannels = async () => {
      try {
        setLoading(true);
        const [response, error] = await channelService.getAll({
          limit: 1000 // Buscar todos os canais disponíveis
        });

        if (error) {
          console.error("Erro ao carregar canais:", error);
          setError("Erro ao carregar canais");
          return;
        }

        if (response) {
          setChannels(response.result);
        }
      } catch (err) {
        console.error("Erro ao carregar canais:", err);
        setError("Erro ao carregar canais");
      } finally {
        setLoading(false);
      }
    };

    fetchChannels();
  }, []);

  // Formatar canais para o react-select
  const channelOptions = channels
    .filter(channel => channel.isActive)
    .map((channel) => ({
      value: channel._id,
      label: channel.name,
    }));

  const selectedOptions = channelOptions.filter((option) =>
    selectedChannels.includes(option.value)
  );

  const handleChannelChange = (selected: any) => {
    const selectedValues = selected
      ? selected.map((item: any) => item.value)
      : [];
    onChange(selectedValues);
  };

  // Custom styles for react-select to match the site design
  const customStyles = {
    control: (provided: any, state: any) => ({
      ...provided,
      borderRadius: "0.375rem",
      borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))",
      boxShadow: state.isFocused ? "0 0 0 2px hsl(var(--ring))" : "none",
      "&:hover": {
        borderColor: state.isFocused ? "hsl(var(--ring))" : "hsl(var(--input))",
      },
      background: "hsl(var(--background))",
      color: "#fff",
    }),
    menu: (provided: any) => ({
      ...provided,
      background: "hsl(var(--popover))",
      borderRadius: "0.375rem",
      border: "1px solid hsl(var(--border))",
      boxShadow:
        "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
    }),
    option: (provided: any, state: any) => ({
      ...provided,
      background: state.isSelected
        ? "hsl(var(--primary))"
        : state.isFocused
          ? "hsl(var(--accent))"
          : "transparent",
      color: state.isSelected
        ? "hsl(var(--primary-foreground))"
        : "hsl(var(--foreground))",
      cursor: "pointer",
    }),
    multiValue: (provided: any) => ({
      ...provided,
      background: "hsl(var(--accent))",
      borderRadius: "0.25rem",
    }),
    multiValueLabel: (provided: any) => ({
      ...provided,
      color: "hsl(var(--accent-foreground))",
    }),
    multiValueRemove: (provided: any) => ({
      ...provided,
      color: "hsl(var(--accent-foreground))",
      ":hover": {
        background: "hsl(var(--destructive))",
        color: "hsl(var(--destructive-foreground))",
      },
    }),
  };

  return (
    <div className="w-full">
      <Select
        isMulti
        options={channelOptions}
        value={selectedOptions}
        onChange={handleChannelChange}
        styles={customStyles}
        placeholder={loading ? "Carregando..." : "Selecione os canais"}
        isLoading={loading}
        isDisabled={loading}
        noOptionsMessage={() => "Nenhum canal encontrado"}
        loadingMessage={() => "Carregando..."}
      />
      {error && <p className="text-sm text-red-500 mt-1">{error}</p>}
    </div>
  );
}
